import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { TopBar } from "@/components/TopBar";
import { PayPalButton } from "@/components/PayPalButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CheckCircle2, Info } from "lucide-react";

export default function BookingForm({ mode }) {
  const { t } = useApp();
  const nav = useNavigate();
  const isRiding = mode === "riding_lesson";
  const [form, setForm] = useState({
    name: "", email: "", phone: "", horse_name: "",
    case_description: "", preferred_date: "", package: "single",
  });
  const [pkg, setPkg] = useState("single");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/consultations", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        horse_name: form.horse_name,
        case_description: form.case_description,
        preferred_date: form.preferred_date,
        type: mode,
        package: isRiding ? pkg : null,
      });
      toast.success(t("t_booked"));
      setDone(true);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || t("t_error"));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, testid, type = "text") => (
    <div>
      <Label className="mb-1.5 block text-sm">{t(key)}</Label>
      <Input
        data-testid={testid}
        type={type}
        value={form[key]}
        onChange={set(key)}
        required={key === "name" || key === "email"}
        className="rounded-[12px] border-input focus-visible:ring-primary"
      />
    </div>
  );

  return (
    <div className="min-h-screen pb-28">
      <TopBar showBack backTo="/" />
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
        {done ? (
          <div className="animate-fade-up rounded-[20px] border border-border bg-card p-10 text-center shadow-[0_8px_30px_rgba(62,97,72,0.06)]">
            <CheckCircle2 className="mx-auto h-16 w-16 text-primary" strokeWidth={1.5} />
            <h2 className="mt-4 font-serif-h text-3xl text-foreground" data-testid="success-title">{t("success_title")}</h2>
            <p className="mx-auto mt-3 max-w-md text-muted-foreground">{t("success_msg")}</p>
            <div className="mt-8 flex flex-col items-center gap-3">
              <PayPalButton testid="success-paypal" />
              <Button variant="ghost" className="rounded-full" onClick={() => nav("/")} data-testid="success-home">
                {t("back")}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="animate-fade-up space-y-5">
            <h1 className="font-serif-h text-3xl text-foreground" data-testid="booking-title">
              {isRiding ? t("riding_title") : t("consultation_title")}
            </h1>

            <div className="grid gap-4 sm:grid-cols-2">
              {field("name", "input-name")}
              {field("email", "input-email", "email")}
              {field("phone", "input-phone")}
              {field("horse_name", "input-horse-name")}
            </div>

            {isRiding && field("preferred_date", "input-preferred-date")}

            <div>
              <Label className="mb-1.5 block text-sm">
                {isRiding ? t("experience_notes") : t("case_description")}
              </Label>
              <Textarea
                data-testid="input-description"
                value={form.case_description}
                onChange={set("case_description")}
                rows={4}
                className="rounded-[12px] border-input focus-visible:ring-primary"
              />
            </div>

            {isRiding && (
              <div className="rounded-[12px] border border-border bg-card p-4">
                <Label className="mb-3 block text-sm font-semibold">{t("package")}</Label>
                <RadioGroup value={pkg} onValueChange={setPkg} className="space-y-3">
                  <label className="flex cursor-pointer items-center gap-3" data-testid="pkg-single">
                    <RadioGroupItem value="single" />
                    <span>{t("pkg_single")}</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-3" data-testid="pkg-ten">
                    <RadioGroupItem value="ten_pack" />
                    <span>{t("pkg_ten")}</span>
                  </label>
                </RadioGroup>
              </div>
            )}

            <div className="flex items-start gap-3 rounded-[12px] bg-[#FBF3E7] p-4 text-sm text-[#1A2C21]">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" strokeWidth={1.5} />
              <span>{isRiding ? t("riding_hint") : t("consultation_hint")}</span>
            </div>

            <Button
              type="submit"
              disabled={busy}
              data-testid="booking-submit"
              className="w-full rounded-full bg-primary py-6 text-base font-medium text-primary-foreground transition-transform hover:-translate-y-0.5 hover:bg-[#2C4633]"
            >
              {t("submit")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
